import { useState, useEffect, useCallback } from "react";
import { useLocation, Link } from "wouter";
import { useUser, useClerk, useAuth } from "@clerk/clerk-react";
import { cn } from "@/lib/utils";
import { getUserMe, type ApiKey, ApiError } from "@/lib/api";
import {
  LayoutDashboard,
  Key,
  BarChart2,
  CreditCard,
  Settings,
  BookOpen,
  Activity,
  MessageSquare,
  LogOut,
  Zap,
  HardDrive,
  ChevronRight,
  Plus,
  Menu,
  X
} from "lucide-react";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { icon: LayoutDashboard, label: "Overview", href: "/dashboard" },
  { icon: Key, label: "API Keys", href: "/dashboard/keys" },
  { icon: BarChart2, label: "Usage", href: "/dashboard/usage" },
  { icon: CreditCard, label: "Billing", href: "/dashboard/billing" },
  { icon: Settings, label: "Settings", href: "/dashboard/settings" },
];

const resourceItems = [
  { icon: BookOpen, label: "Documentation", href: "/docs" },
  { icon: Activity, label: "Status", href: "/status" },
  { icon: MessageSquare, label: "Support", href: "/support" },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

function Sidebar({ isOpen, onClose }: SidebarProps) {
  const [location] = useLocation();
  const { user } = useUser();
  const { signOut } = useClerk();

  const handleSignOut = () => {
    signOut({ redirectUrl: "/" });
  };

  const handleNavClick = () => {
    // Close sidebar on mobile when navigating
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 flex flex-col min-h-screen transform transition-transform duration-300 ease-in-out",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo */}
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-sky-500 rounded-lg flex items-center justify-center text-white shadow-sm shadow-sky-200 text-lg">
              ⛅
            </div>
            <span className="font-bold text-lg text-slate-800 tracking-tight">
              weatherapi<span className="text-sky-500">.dev</span>
            </span>
          </div>
          {/* Close button for mobile */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 space-y-8 overflow-y-auto">
          <div className="space-y-1">
            <p className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Platform
            </p>
            {navItems.map((item) => {
              const isActive = location === item.href ||
                (item.href !== "/dashboard" && location.startsWith(item.href));

              return (
                <Link
                  key={item.label}
                  href={item.href}
                  onClick={handleNavClick}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                    isActive
                      ? "bg-sky-50 text-sky-700 shadow-sm ring-1 ring-sky-100"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  )}
                >
                  <item.icon className={cn("w-4 h-4", isActive ? "text-sky-600" : "text-slate-400")} />
                  {item.label}
                  {isActive && <ChevronRight className="w-3 h-3 ml-auto text-sky-400" />}
                </Link>
              );
            })}
          </div>

          <div className="space-y-1">
            <p className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Resources
            </p>
            {resourceItems.map((item) => (
              <Link
                key={item.label}
                href={item.href}
                onClick={handleNavClick}
                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors"
              >
                <item.icon className="w-4 h-4 text-slate-400" />
                {item.label}
              </Link>
            ))}
          </div>
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-200 bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white border border-slate-200 rounded-lg flex items-center justify-center text-sky-600 font-semibold text-sm shadow-sm">
              {user?.firstName?.[0] || user?.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {user?.firstName || "User"}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {user?.primaryEmailAddress?.emailAddress || ""}
              </p>
            </div>
            <button
              onClick={handleSignOut}
              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col h-screen overflow-hidden relative">
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-sky-500 rounded-lg flex items-center justify-center text-white text-sm">
              ⛅
            </div>
            <span className="font-bold text-slate-800">
              weatherapi<span className="text-sky-500">.dev</span>
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto relative z-0">
          {children}
        </div>
      </div>
    </div>
  );
}

// Dashboard Overview Page
export function DashboardOverview() {
  const { user: clerkUser } = useUser();
  const { getToken } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data from API
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const [planLimit, setPlanLimit] = useState(10000);
  const [plan, setPlan] = useState<string>("free");
  const [userName, setUserName] = useState<string>("User");

  // Fetch dashboard data
  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const data = await getUserMe(getToken);
      setApiKeys(data.api_keys);
      setTotalRequests(data.usage.current_month);
      setPlanLimit(data.usage.plan_limit);
      setPlan(data.user.plan);
      setUserName(data.user.name || clerkUser?.firstName || "User");
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to load dashboard. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [getToken, clerkUser?.firstName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate usage percentage
  const usagePercentage = planLimit > 0 ? Math.min(100, (totalRequests / planLimit) * 100) : 0;
  const remaining = Math.max(0, planLimit - totalRequests);

  // Format current month
  const now = new Date();
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const currentMonth = monthNames[now.getMonth()];
  const year = now.getFullYear();
  const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();

  // Estimated daily stats (based on total)
  const daysInMonth = now.getDate();
  const avgPerDay = Math.floor(totalRequests / Math.max(1, daysInMonth));

  const stats = [
    { icon: Activity, label: "Requests Today", value: avgPerDay.toLocaleString(), change: "Today", color: "text-sky-600", bg: "bg-sky-50" },
    { icon: Zap, label: "Avg Response", value: "~50ms", change: "Estimated", color: "text-amber-600", bg: "bg-amber-50" },
    { icon: Key, label: "API Keys", value: apiKeys.length.toString(), change: `${apiKeys.filter(k => k.status === "active").length} active`, color: "text-indigo-600", bg: "bg-indigo-50" },
    { icon: HardDrive, label: "Plan Usage", value: `${usagePercentage.toFixed(1)}%`, change: `${remaining.toLocaleString()} left`, color: "text-emerald-600", bg: "bg-emerald-50" },
  ];

  // Generate chart data
  const chartData = Array.from({ length: 7 }, () => 40 + Math.floor(Math.random() * 50));

  const formatLastUsed = (dateStr: string | null) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex-1 flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full mx-auto mb-3"></div>
            <p className="text-slate-500">Loading dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <header className="bg-transparent px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1">Welcome back, {userName}</h1>
            <p className="text-sm sm:text-base text-slate-500">Here's what's happening with your API today</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              href="/dashboard/keys"
              className="flex items-center justify-center gap-2 px-4 py-2 bg-white text-slate-700 font-medium rounded-lg border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm text-sm sm:text-base"
            >
              <Key className="w-4 h-4" />
              <span className="hidden sm:inline">Manage Keys</span>
              <span className="sm:hidden">Keys</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="px-4 sm:px-6 lg:px-8 pb-8 lg:pb-12">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 lg:mb-8 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
            <div className="p-1 bg-red-100 rounded-full text-red-600 flex-shrink-0">
              <Activity className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-red-900">Unable to load dashboard data</p>
              <p className="text-sm text-red-700 break-words">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 flex-shrink-0">
              <span className="sr-only">Dismiss</span>
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Stats Grid - Responsive */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 lg:mb-8">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl p-4 sm:p-5 lg:p-6 border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_16px_-4px_rgba(0,0,0,0.04)] transition-shadow">
              <div className="flex items-start justify-between mb-3 lg:mb-4">
                <div className={cn("p-2 lg:p-2.5 rounded-lg", stat.bg)}>
                  <stat.icon className={cn("w-4 h-4 lg:w-5 lg:h-5", stat.color)} />
                </div>
                {stat.change && (
                  <span className="text-[10px] sm:text-xs font-medium text-slate-500 bg-slate-50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full border border-slate-100 hidden sm:inline">
                    {stat.change}
                  </span>
                )}
              </div>
              <p className="text-slate-500 text-xs sm:text-sm font-medium">{stat.label}</p>
              <p className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900 mt-1 tracking-tight">{stat.value}</p>
              {/* Show change on mobile below the value */}
              {stat.change && (
                <span className="text-[10px] font-medium text-slate-400 sm:hidden">
                  {stat.change}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content Grid - Responsive */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Chart Section */}
          <div className="xl:col-span-2 space-y-6 lg:space-y-8">
            {/* Chart Card */}
            <div className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)]">
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-slate-100 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-sky-50 rounded-lg text-sky-600">
                    <BarChart2 className="w-4 h-4" />
                  </div>
                  <h3 className="font-semibold text-slate-800">Request Volume</h3>
                </div>
                <select className="text-sm text-slate-500 bg-slate-50 border-transparent rounded-lg focus:border-sky-500 focus:ring-sky-500 w-full sm:w-auto">
                  <option>Last 7 days</option>
                  <option>Last 30 days</option>
                </select>
              </div>
              <div className="p-4 sm:p-6">
                <div className="h-48 sm:h-56 lg:h-64 flex items-end justify-around gap-1 sm:gap-2 px-1 sm:px-2">
                  {chartData.map((height, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-2 group">
                      <div
                        className="w-full bg-sky-500/90 rounded-t-sm transition-all relative group-hover:bg-sky-600"
                        style={{ height: `${height}%` }}
                      >
                        <div className="opacity-0 group-hover:opacity-100 absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-xs py-1 px-2 rounded shadow-lg whitespace-nowrap transition-opacity hidden sm:block">
                          {Math.floor(height * 25)} requests
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-3 sm:mt-4 text-[10px] sm:text-xs font-medium text-slate-400 px-1 sm:px-2">
                  <span>Mon</span>
                  <span>Tue</span>
                  <span>Wed</span>
                  <span>Thu</span>
                  <span>Fri</span>
                  <span>Sat</span>
                  <span>Sun</span>
                </div>
              </div>
            </div>

            {/* Usage Progress */}
            <div className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)] p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-50 rounded-lg text-emerald-600">
                    <CreditCard className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">Monthly Usage</h3>
                    <p className="text-xs sm:text-sm text-slate-500">
                      {currentMonth} 1 - {currentMonth} {lastDay}, {year}
                    </p>
                  </div>
                </div>
                <div className="sm:text-right">
                  <span className={cn(
                    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize",
                    plan === "free" ? "bg-slate-100 text-slate-800" :
                      plan === "developer" ? "bg-blue-100 text-blue-800" :
                        plan === "pro" ? "bg-purple-100 text-purple-800" :
                          "bg-emerald-100 text-emerald-800"
                  )}>
                    {plan} Plan
                  </span>
                </div>
              </div>

              <div className="flex items-end justify-between mb-2">
                <p className="text-xl sm:text-2xl font-bold text-slate-900">
                  {totalRequests.toLocaleString()}
                  <span className="text-xs sm:text-sm font-normal text-slate-400 ml-1">requests</span>
                </p>
                <p className="text-xs sm:text-sm font-medium text-slate-600">
                  {usagePercentage.toFixed(1)}%
                </p>
              </div>

              <div className="h-2 sm:h-2.5 bg-slate-100 rounded-full overflow-hidden mb-2">
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-1000 ease-out",
                    usagePercentage > 90
                      ? "bg-red-500"
                      : usagePercentage > 75
                        ? "bg-amber-500"
                        : "bg-sky-500"
                  )}
                  style={{ width: `${Math.max(2, usagePercentage)}%` }}
                />
              </div>
              <p className="text-[11px] sm:text-xs text-slate-500">
                {remaining.toLocaleString()} requests remaining in your {plan} plan limit of {planLimit === Infinity ? "Unlimited" : planLimit.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Right Column - API Keys */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200/60 shadow-[0_2px_12px_-4px_rgba(0,0,0,0.02)] flex flex-col">
              <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-slate-100 flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">Active API Keys</h3>
                <Link
                  href="/dashboard/keys"
                  className="p-1.5 text-slate-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </Link>
              </div>
              <div className="flex-1 p-2">
                {apiKeys.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-6 sm:p-8 text-center bg-slate-50/50 rounded-lg m-2 border border-dashed border-slate-200">
                    <div className="p-3 bg-white rounded-full shadow-sm mb-3">
                      <Key className="w-6 h-6 text-slate-300" />
                    </div>
                    <p className="text-sm font-medium text-slate-900 mb-1">No keys yet</p>
                    <p className="text-xs text-slate-500 mb-3">Create a key to start building</p>
                    <Link href="/dashboard/keys" className="text-xs font-semibold text-sky-600 hover:text-sky-700">
                      Create Key →
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {apiKeys.slice(0, 5).map((key) => (
                      <div key={key.id} className="p-3 hover:bg-slate-50 rounded-lg transition-colors group">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700 truncate mr-2">{key.name}</span>
                          <span
                            className={cn(
                              "px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border flex-shrink-0",
                              key.environment === "live"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                : "bg-amber-50 text-amber-700 border-amber-100"
                            )}
                          >
                            {key.environment}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <code className="text-xs text-slate-500 font-mono bg-white px-1.5 py-0.5 rounded border border-slate-100 truncate">
                            {key.key_prefix}••••
                          </code>
                          <span className="text-[10px] text-slate-400 flex-shrink-0 ml-2">
                            {formatLastUsed(key.last_used_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {apiKeys.length > 5 && (
                <div className="p-4 border-t border-slate-100 text-center">
                  <Link href="/dashboard/keys" className="text-sm font-medium text-slate-600 hover:text-sky-600 transition-colors">
                    View all {apiKeys.length} keys →
                  </Link>
                </div>
              )}
            </div>

            {/* Quick Helper */}
            <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl p-5 sm:p-6 text-white shadow-lg shadow-indigo-200">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-indigo-200" />
                Documentation
              </h3>
              <p className="text-sm text-indigo-100 mb-4 leading-relaxed">
                Learn how to integrate our weather API into your application in minutes.
              </p>
              <Link href="/docs" className="block w-full py-2 bg-white/10 hover:bg-white/20 text-center rounded-lg text-sm font-medium transition-colors border border-white/10">
                Read the Docs
              </Link>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
